<!-- Holdings: sortable instruments table; click opens details. -->
<script lang="ts">
  import * as E from "../core/engine";
  import { app, need, PERIOD_NAMES } from "../lib/state.svelte";
  import { openInstrument } from "../lib/actions";
  import { fmtDate, fmtNum, fmtQty } from "../lib/format";
  import { instrColor } from "../lib/chart";
  import { groupBondRows, type HoldRow } from "../lib/holdings";
  import ViewHead from "../components/ViewHead.svelte";
  import Card from "../components/Card.svelte";
  import Toggle from "../components/Toggle.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";
  import Pct from "../components/Pct.svelte";

  const m = $derived(need(app.model));
  const A = $derived(need(app.agg));
  const r = $derived(app.range);
  const showClosed = $derived(!!app.ui.showClosed);
  const groupBonds = $derived(app.ui.groupBonds !== false);
  const hold = $derived.by(() => {
    const raw = E.holdings(m, app.accList, r.start, r.end, { includeClosed: showClosed }).filter((h) => showClosed || h.qty > 1e-9 || Math.abs(h.plPeriod) > 0.01);
    return groupBonds ? groupBondRows(raw) : (raw as HoldRow[]);
  });
  const e = $derived(m.idx(r.end));
  const tot = $derived(hold.reduce((s, h) => ({ value: s.value + h.value, cost: s.cost + h.cost, pl: s.pl + h.plPeriod, day: s.day + h.dayChange, real: s.real + h.realizedTotal, unT: s.unT + (h.valueT - h.costT) }), { value: 0, cost: 0, pl: 0, day: 0, real: 0, unT: 0 }));
  const tr = $derived(m.taxRate);

  // sorting: click a header; text ascending, numbers descending
  type Key = keyof HoldRow;
  let key = $state<Key>("value");
  let dir = $state<"asc" | "desc">("desc");
  const sortBy = (k: Key, text = false) => {
    if (key === k) dir = dir === "asc" ? "desc" : "asc";
    else { key = k; dir = text ? "asc" : "desc"; }
  };
  const norm = (v: unknown) => (v === null || v === undefined || (typeof v === "number" && isNaN(v)) ? -Infinity : v);
  const sorted = $derived([...hold].sort((a, b) => {
    const x = norm(a[key]) as number | string, y = norm(b[key]) as number | string;
    const c = typeof x === "string" && typeof y === "string" ? x.localeCompare(y, "pl") : x < y ? -1 : x > y ? 1 : 0;
    return dir === "asc" ? c : -c;
  }));
  const thCls = (k: Key) => `sortable${key === k ? " sorted" : ""}${key === k && dir === "asc" ? " asc" : ""}`;

  function openRow(h: HoldRow) {
    if (h.key.startsWith("BONDS-")) { app.ui.groupBonds = false; return; }
    openInstrument(h.key);
  }
</script>

<ViewHead title="Pozycje" sub="stan na {fmtDate(r.end)} · wynik okresu: {PERIOD_NAMES[app.period]}" />
<Card title="Instrumenty" hint="kliknij wiersz, aby zobaczyć historię zakupów">
  {#snippet extra()}
    <div class="row">
      <Toggle small id="groupBonds" checked={groupBonds} onchange={(v) => (app.ui.groupBonds = v)}>zwiń obligacje</Toggle>
      <Toggle small id="showClosed" checked={showClosed} onchange={(v) => (app.ui.showClosed = v)}>pokaż zamknięte</Toggle>
    </div>
  {/snippet}
  <div class="table-wrap"><table id="tblHold"><thead><tr>
      <th class="l {thCls('name')}" data-k="name" data-type="text" onclick={() => sortBy("name", true)}>Instrument</th>
      <th class={thCls("qty")} data-k="qty" onclick={() => sortBy("qty")}>Ilość</th>
      <th class={thCls("avgCost")} data-k="avgCost" onclick={() => sortBy("avgCost")}>Śr. koszt</th><th>Kurs</th>
      <th class={thCls("value")} data-k="value" onclick={() => sortBy("value")}>Wartość</th>
      <th class={thCls("weight")} data-k="weight" onclick={() => sortBy("weight")}>Udział</th>
      <th class={thCls("unrealized")} data-k="unrealized" onclick={() => sortBy("unrealized")}>Zysk niezreal.</th>
      <th class={thCls("unrealizedPct")} data-k="unrealizedPct" onclick={() => sortBy("unrealizedPct")}>%</th>
      {#if app.tax}<th class={thCls("unrealized")} data-k="unrealized" onclick={() => sortBy("unrealized")}>Po podatku</th>{/if}
      <th class={thCls("plPeriod")} data-k="plPeriod" onclick={() => sortBy("plPeriod")}>Wynik w okresie</th>
      <th class={thCls("dayChange")} data-k="dayChange" onclick={() => sortBy("dayChange")}>Ost. dzień</th>
      <th class={thCls("realizedTotal")} data-k="realizedTotal" onclick={() => sortBy("realizedTotal")}>Zrealizowany</th>
    </tr></thead>
    <tbody>
      {#each sorted as h (h.key)}
        {@const l = h.listings[0]}
        <tr class="clickable" data-key={h.key} onclick={() => openRow(h)}>
          <td class="l"><div class="name-cell"><span><span class="dot" style="background:{instrColor(h.key)}"></span>{h.name}</span><small>{h.symbols.join(", ")}{h.isin ? " · " + h.isin : ""}</small></div></td>
          <td>{fmtQty(h.qty)}</td><td>{#if h.avgCost}<Money v={h.avgCost} d={2} />{:else}–{/if}</td>
          <td>{l ? `${fmtNum(l.price, 2)} ${l.ccy}` : "–"}{#if h.listings.length > 1}{" "}<span class="muted small">+{h.listings.length - 1}</span>{/if}</td>
          <td><Money v={h.value} /></td><td>{fmtNum(h.weight * 100, 1)}%<span class="bar-inline" style="width:{Math.round(h.weight * 60)}px"></span></td>
          <td><Signed v={h.unrealized} /></td><td><Pct v={h.unrealizedPct} /></td>{#if app.tax}<td><Signed v={h.unrealized - tr * Math.max(0, h.valueT - h.costT)} /></td>{/if}
          <td><Signed v={h.plPeriod} /></td><td><Signed v={h.dayChange} /></td><td>{#if h.realizedTotal}<Signed v={h.realizedTotal} />{:else}–{/if}</td>
        </tr>
      {/each}
    </tbody>
    <tfoot>
      <tr class="total"><td class="l">Razem papiery</td><td></td><td></td><td></td><td><Money v={tot.value} /></td><td></td><td><Signed v={tot.value - tot.cost} /></td><td><Pct v={tot.cost > 0 ? tot.value / tot.cost - 1 : null} /></td>
        {#if app.tax}<td><Signed v={tot.value - tot.cost - tr * Math.max(0, tot.unT)} /></td>{/if}<td><Signed v={tot.pl} /></td><td><Signed v={tot.day} /></td><td><Signed v={tot.real} /></td></tr>
      <tr class="sub"><td class="l">Gotówka na rachunkach</td><td></td><td></td><td></td><td><Money v={A.cash[e]} /></td><td colspan="8"></td></tr>
    </tfoot>
  </table></div>
</Card>
